import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, ArrowRight } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

function PersonSelect({ label, value, onChange, people, testId }) {
  return (
    <div><Label>{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Choose…" /></SelectTrigger>
        <SelectContent>{people.map((m) => <SelectItem key={m.id} value={m.id}>{m.name} · {m.role}</SelectItem>)}</SelectContent>
      </Select></div>
  );
}

function Preview({ preview }) {
  if (!preview) return null;
  return (
    <div className="rounded-xl border border-gold/30 bg-gold-light/50 p-3 text-sm" data-testid="handover-preview">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold">{preview.outgoing.name}</span><span className="text-xs text-muted-foreground">({preview.outgoing.role})</span>
        <ArrowRight size={14} className="text-crimson" />
        <span className="font-semibold">{preview.incoming.name}</span><span className="text-xs text-muted-foreground">(currently {preview.incoming.role})</span>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">Successor receives <span className="font-mono-accent text-crimson">{preview.role}</span> with {preview.permissions.length} permission{preview.permissions.length === 1 ? "" : "s"}: {preview.permissions.join(", ") || "none"}.
        {preview.transfers.committees > 0 && ` Also moves ${preview.transfers.committees} committee seat(s).`}</p>
    </div>
  );
}

export function OfficerHandover({ roles, onDone }) {
  const [open, setOpen] = useState(false);
  const { data: members } = useApi(open ? "/admin/members" : null);
  const [form, setForm] = useState({ from_id: "", to_id: "", outgoing_role: "member", note: "" });
  const [preview, setPreview] = useState(null);
  const list = members || [];
  const officers = list.filter((m) => m.role && m.role !== "member");
  useEffect(() => {
    if (!form.from_id || !form.to_id || form.from_id === form.to_id) { setPreview(null); return; }
    api.get(`/admin/handover/preview?from_id=${form.from_id}&to_id=${form.to_id}`).then((r) => setPreview(r.data)).catch(() => setPreview(null));
  }, [form.from_id, form.to_id]);
  const submit = async () => {
    try { const { data } = await api.post("/admin/handover", form); toast.success(`${data.role} duties handed over`); setOpen(false); setForm({ from_id: "", to_id: "", outgoing_role: "member", note: "" }); onDone?.(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" className="border-gold text-crimson hover:bg-gold-light" data-testid="handover-btn"><ArrowRightLeft size={16} className="mr-1" /> Officer handover</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Officer handover</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Move one officer's role, permissions and committee seats to a successor in one step. Both are notified.</p>
        <div className="space-y-3">
          <PersonSelect label="Outgoing officer" value={form.from_id} onChange={(v) => setForm({ ...form, from_id: v })} people={officers} testId="handover-from" />
          <PersonSelect label="Successor" value={form.to_id} onChange={(v) => setForm({ ...form, to_id: v })} people={list.filter((m) => m.id !== form.from_id && m.status !== "pending")} testId="handover-to" />
          <div><Label>Outgoing officer's new role</Label>
            <Select value={form.outgoing_role} onValueChange={(v) => setForm({ ...form, outgoing_role: v })}>
              <SelectTrigger data-testid="handover-outgoing-role"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="member">member</SelectItem>{(roles || []).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select></div>
          <div><Label>Note (e.g. "Elected at 2026 AGM")</Label><Input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="handover-note" /></div>
          <Preview preview={preview} />
        </div>
        <DialogFooter><Button className="bg-crimson" disabled={!preview} onClick={submit} data-testid="handover-confirm">Confirm handover</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function HandoverHistory() {
  const { data } = useApi("/admin/handover/history");
  if (!data?.length) return null;
  return (
    <section data-testid="handover-history"><h3 className="eyebrow mb-2 text-gold">Recent handovers</h3>
      <div className="glass divide-y divide-gold/15 rounded-2xl text-sm">
        {data.slice(0, 8).map((h) => (
          <div key={h.id} className="flex flex-wrap items-center gap-2 px-4 py-2" data-testid={`handover-${h.id}`}>
            <span className="font-mono-accent text-xs text-muted-foreground">{h.timestamp?.slice(0, 10)}</span>
            <span className="font-semibold text-crimson">{h.role}</span>: {h.from_name} <ArrowRight size={12} /> {h.to_name}
            {h.note && <span className="text-xs text-muted-foreground">— {h.note}</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
